/**
 * Thin fetch client for /api/admin/bot-ops.
 *
 * Deliberately NOT services/api.ts: that client throws a plain Error and drops
 * the HTTP status, so a page cannot tell a 403 from a 503 or read Retry-After,
 * and Bot Ops needs all three (plus ETag/304). Same-origin, cookie auth, exactly
 * like the rest of the admin app.
 */

const BASE = '/api/admin/bot-ops';

export class BotOpsHttpError extends Error {
  status: number;
  code: string | null;
  retryAfter: number | null;

  constructor(message: string, status: number, code: string | null = null, retryAfter: number | null = null) {
    super(message);
    this.name = 'BotOpsHttpError';
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

export interface BotOpsResponse<T> {
  status: number;
  notModified: boolean;
  data: T | null;
  meta: Record<string, unknown> | null;
  etag: string | null;
}

export async function botOpsFetch<T>(
  path: string,
  init: { method?: string; body?: unknown; etag?: string | null; headers?: Record<string, string> } = {},
): Promise<BotOpsResponse<T>> {
  const headers: Record<string, string> = { Accept: 'application/json', ...(init.headers || {}) };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (init.etag) headers['If-None-Match'] = init.etag;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: init.method || 'GET',
      credentials: 'include',
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch (e) {
    // Offline, DNS, CORS: no response at all.
    throw new BotOpsHttpError('Could not reach the server', 0, 'NETWORK');
  }

  if (res.status === 304) {
    return { status: 304, notModified: true, data: null, meta: null, etag: init.etag || null };
  }

  let body: { success?: boolean; data?: T; meta?: Record<string, unknown>; error?: string; code?: string } | null = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok || (body && body.success === false)) {
    const retry = res.headers.get('Retry-After');
    throw new BotOpsHttpError(
      (body && body.error) || `Request failed (HTTP ${res.status})`,
      res.status,
      (body && body.code) || null,
      retry ? Number(retry) || null : null,
    );
  }

  return {
    status: res.status,
    notModified: false,
    data: body && body.data !== undefined ? (body.data as T) : null,
    meta: (body && body.meta) || null,
    etag: res.headers.get('ETag'),
  };
}
